import { normalizeSpaceValue } from "@/ui/utils/spacings"
import { classNames, ResponsiveValues, setCssVar } from "@punks/ui-core"

export type ResponsiveColumns = ResponsiveValues<number>
export type ResponsiveGap = ResponsiveValues<number>

export interface GridProps {
  children: React.ReactNode
  className?: string
  gap?: number | ResponsiveGap
  cols?: number | ResponsiveColumns
}

export const Grid = ({
  children,
  className,
  gap = 4,
  cols = 12,
}: GridProps) => (
  <div
    style={{
      // columns
      ...setCssVar(
        "--c-xs",
        `repeat(${(typeof cols === "number" ? cols : (cols as ResponsiveColumns)?.xs) ?? 1}, minmax(0, 1fr))`
      ),
      ...((cols as ResponsiveColumns)?.sm &&
        setCssVar(
          "--c-sm",
          `repeat(${(cols as ResponsiveColumns).sm}, minmax(0, 1fr))`
        )),
      ...((cols as ResponsiveColumns)?.md &&
        setCssVar(
          "--c-md",
          `repeat(${(cols as ResponsiveColumns).md}, minmax(0, 1fr))`
        )),
      ...((cols as ResponsiveColumns)?.lg &&
        setCssVar(
          "--c-lg",
          `repeat(${(cols as ResponsiveColumns).lg}, minmax(0, 1fr))`
        )),
      ...((cols as ResponsiveColumns)?.xl &&
        setCssVar(
          "--c-xl",
          `repeat(${(cols as ResponsiveColumns).xl}, minmax(0, 1fr))`
        )),
      // gap
      ...setCssVar(
        "--g-xs",
        normalizeSpaceValue(typeof gap === "number" ? gap : gap.xs)
      ),
      ...((cols as ResponsiveColumns)?.sm &&
        setCssVar("--g-sm", normalizeSpaceValue((gap as ResponsiveGap).sm))),
      ...((cols as ResponsiveColumns)?.md &&
        setCssVar("--g-md", normalizeSpaceValue((gap as ResponsiveGap).md))),
      ...((cols as ResponsiveColumns)?.lg &&
        setCssVar("--g-lg", normalizeSpaceValue((gap as ResponsiveGap).lg))),
      ...((cols as ResponsiveColumns)?.xl &&
        setCssVar("--g-xl", normalizeSpaceValue((gap as ResponsiveGap).xl))),
    }}
    className={classNames(
      "grid grid-cols-[var(--c-xs)]",
      {
        "sm:grid-cols-[var(--c-sm)]": !!(cols as ResponsiveColumns)?.sm,
        "md:grid-cols-[var(--c-md)]": !!(cols as ResponsiveColumns)?.md,
        "lg:grid-cols-[var(--c-lg)]": !!(cols as ResponsiveColumns)?.lg,
        "xl:grid-cols-[var(--c-xl)]": !!(cols as ResponsiveColumns)?.xl,
      },
      "gap-[var(--g-xs)]",
      {
        "sm:gap-[var(--g-sm)]": !!(gap as ResponsiveColumns)?.sm,
        "md:gap-[var(--g-md)]": !!(gap as ResponsiveColumns)?.md,
        "lg:gap-[var(--g-lg)]": !!(gap as ResponsiveColumns)?.lg,
        "xl:gap-[var(--g-xl)]": !!(gap as ResponsiveColumns)?.xl,
      },
      className
    )}
  >
    {children}
  </div>
)

export interface GridItemProps {
  children: React.ReactNode
  className?: string
  colSpan?: number | ResponsiveColumns
}

export const GridItem = ({
  children,
  className = "",
  colSpan = 1,
}: GridItemProps) => (
  <div
    style={{
      ...setCssVar(
        "--cs-xs",
        `span ${
          (typeof colSpan === "number"
            ? colSpan
            : (colSpan as ResponsiveColumns)?.xs) ?? 1
        } / span ${
          (typeof colSpan === "number"
            ? colSpan
            : (colSpan as ResponsiveColumns)?.xs) ?? 1
        }`
      ),
      ...((colSpan as ResponsiveColumns)?.sm &&
        setCssVar(
          "--cs-sm",
          `span ${(colSpan as ResponsiveColumns).sm} / span ${(colSpan as ResponsiveColumns).sm}`
        )),
      ...((colSpan as ResponsiveColumns)?.md &&
        setCssVar(
          "--cs-md",
          `span ${(colSpan as ResponsiveColumns).md} / span ${(colSpan as ResponsiveColumns).md}`
        )),
      ...((colSpan as ResponsiveColumns)?.lg &&
        setCssVar(
          "--cs-lg",
          `span ${(colSpan as ResponsiveColumns).lg} / span ${(colSpan as ResponsiveColumns).lg}`
        )),
      ...((colSpan as ResponsiveColumns)?.xl &&
        setCssVar(
          "--cs-xl",
          `span ${(colSpan as ResponsiveColumns).xl} / span ${(colSpan as ResponsiveColumns).xl}`
        )),
    }}
    className={classNames(
      "col-[var(--cs-xs)]",
      {
        "sm:col-[var(--cs-sm)]": !!(colSpan as ResponsiveColumns)?.sm,
        "md:col-[var(--cs-md)]": !!(colSpan as ResponsiveColumns)?.md,
        "lg:col-[var(--cs-lg)]": !!(colSpan as ResponsiveColumns)?.lg,
        "xl:col-[var(--cs-xl)]": !!(colSpan as ResponsiveColumns)?.xl,
      },
      className
    )}
  >
    {children}
  </div>
)
